/**
 * FiasAIPopup - Floating popup chat window for arche-specific Fias AI assistance.
 *
 * Renders as a centered overlay taking up most of the screen.
 * Uses plain fixed positioning (not Radix Popover) to avoid focus trapping.
 */

import { useEffect, useRef } from 'react';
import { X, Eraser } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FiasAIConversation } from './FiasAIConversation';
import { FiasAIInput } from './FiasAIInput';
import type { ArcheProposal } from './types';

export interface FiasAIPopupMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  proposals?: ArcheProposal[];
  proposalStatus?: Record<string, 'applied' | 'rejected'>;
}

interface FiasAIPopupProps {
  isOpen: boolean;
  onClose: () => void;
  messages: FiasAIPopupMessage[];
  isStreaming: boolean;
  onSubmit: (message: string) => void;
  onClear: () => void;
  onApplyProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  /** Optional extra content rendered in the popup header (e.g., arche-specific quick-action buttons). */
  headerActions?: React.ReactNode;
}

export function FiasAIPopup({
  isOpen,
  onClose,
  messages,
  isStreaming,
  onSubmit,
  onClear,
  onApplyProposal,
  onRejectProposal,
  headerActions,
}: FiasAIPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 animate-in fade-in duration-200"
        onClick={onClose}
      />
      <div
        ref={popupRef}
        className="fixed inset-8 z-50 max-w-4xl mx-auto flex flex-col rounded-2xl border-[3px] border-primary/70 bg-background shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <img src="/Heptagen_Logo_1.png" alt="Fias" className="h-5 w-5" />
            <span className="font-semibold text-sm">Fias AI</span>
          </div>
          <div className="flex items-center gap-1">
            {headerActions}
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-lg"
                onClick={onClear}
                title="Clear conversation"
                disabled={isStreaming}
              >
                <Eraser className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Message area */}
        <div className="flex-1 overflow-y-auto">
          <FiasAIConversation
            archeId=""
            messages={messages}
            isStreaming={isStreaming}
            onApplyProposal={onApplyProposal}
            onRejectProposal={onRejectProposal}
          />
        </div>

        {/* Input */}
        <FiasAIInput
          onSubmit={onSubmit}
          disabled={isStreaming}
          placeholder="Ask Fias AI for help..."
          autoFocus
        />
      </div>
    </>
  );
}
